/**
 * Test Drive Popup Mini-Preview (iter133g — fidelity fix)
 * ─────────────────────────────────────────────────────────────────────────
 * Renders the EXACT popup element the embed will draw on a dealer site,
 * scaled down via `transform: scale(...)`. Loads the shared popup module
 * (window.VDTestDrivePopup) lazily from /api/test-drive-popup.js so the
 * mini-preview can never drift from production markup.
 * ─────────────────────────────────────────────────────────────────────────
 */
import React, { useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODULE_URL = `${BACKEND_URL}/api/test-drive-popup.js`;

// Cached loader — one script tag, one Promise, shared across previews.
let _modulePromise = null;
function loadPopupModule() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.VDTestDrivePopup) return Promise.resolve(window.VDTestDrivePopup);
  if (_modulePromise) return _modulePromise;
  _modulePromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-vd-td-popup]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.VDTestDrivePopup));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = MODULE_URL;
    s.async = true;
    s.setAttribute("data-vd-td-popup", "1");
    s.addEventListener("load", () => {
      if (window.VDTestDrivePopup) resolve(window.VDTestDrivePopup);
      else reject(new Error("VDTestDrivePopup not on window after load"));
    });
    s.addEventListener("error", () => reject(new Error("failed to load " + MODULE_URL)));
    document.head.appendChild(s);
  });
  return _modulePromise;
}

/**
 * Per-template preview scales. Chosen so the scaled card fits inside
 * the admin's ~400px preview column with a bit of breathing room.
 * Native card widths from the mockup:
 *   A = 392px, B = 560px (with expanded panel), C = 720px.
 * Height is auto — the popup module measures the native height after
 * paint and sizes the host to (nativeHeight × scale) exactly.
 */
const PREVIEW = {
  A: { scale: 0.90 },  // 353px wide — near-native so text stays readable
  B: { scale: 0.62 },  // 347px wide
  C: { scale: 0.48 },  // 346px wide
};

export default function TemplateMiniPreview({ template, preview }) {
  const containerRef = useRef(null);
  const [err, setErr] = useState(null);
  const tpl = String(template || "A").toUpperCase();
  const cfg = PREVIEW[tpl] || PREVIEW.A;

  useEffect(() => {
    if (!preview || !containerRef.current) return;
    let cancelled = false;
    loadPopupModule()
      .then((mod) => {
        if (cancelled || !containerRef.current) return;
        const dealer = preview.dealer_name || "your dealership";
        const cents = preview.denomination_cents;
        const amount =
          typeof cents === "number" && cents > 0 ? `$${Math.round(cents / 100)}` : "$—";
        mod.renderPreview(containerRef.current, {
          template: tpl,
          dealerName: dealer,
          amount,
          branding: preview.branding || {},
          artworkUrl: preview.artwork_url || "",
          consentText: preview.consent_disclosure || "",
          consentVersion: preview.consent_version || "draft_v1",
          fixedMicrocopy: preview.fixed_microcopy || "",
          scale: cfg.scale,
          readOnly: true,
        });
      })
      .catch((e) => {
        // Non-fatal — the admin surface is still usable without the preview.
        console.warn("[TemplateMiniPreview] module load failed", e);
        setErr(e.message || "Preview unavailable");
      });
    return () => {
      cancelled = true;
    };
    // Re-render whenever the branding/dealer/amount/template changes
  }, [
    tpl,
    cfg.scale,
    preview,
    preview?.dealer_name,
    preview?.denomination_cents,
    preview?.artwork_url,
    preview?.branding,
  ]);

  if (!preview) {
    return (
      <div
        className="w-full min-h-[160px] bg-gray-100 animate-pulse rounded-md"
        data-testid={`td-preview-${tpl.toLowerCase()}`}
      />
    );
  }

  return (
    <div
      className="w-full rounded-md border border-gray-200 bg-[#f5f6f8] p-3 flex items-start justify-center relative"
      data-testid={`td-preview-${tpl.toLowerCase()}`}
    >
      <div ref={containerRef} />
      {err && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
          {err}
        </div>
      )}
    </div>
  );
}
